import React, { useMemo } from 'react';
import Divider from '@mui/material/Divider';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import { css } from 'styled-components';
import { Colors, Spacings } from 'styles';
import { SeriesCriteriaCategory } from 'generated/graphql';
import { rootTheme } from 'theme/rootTheme';

export const NOT_SPECIFIED_TEXT = 'Not specified';

// Modality, AnatomicFocus, ContrastStatus are also part of the criteria category response.
// They are not displayed as UX choice (the same info is displayed in the StudyClassificationDisplay).
export const DISPLAY_FIELDS = [
  { key: SeriesCriteriaCategory.ViewOrientation, label: 'View Orientation' },
  { key: SeriesCriteriaCategory.Localizer, label: 'Localizer' },
  { key: SeriesCriteriaCategory.TissueSelection, label: 'Tissue Selection' },
  { key: SeriesCriteriaCategory.FieldOfView, label: 'Field of View' },
  { key: SeriesCriteriaCategory.PatientPosition, label: 'Patient Position' },
  { key: SeriesCriteriaCategory.MrWeighting, label: 'MR Weighting' },
  { key: SeriesCriteriaCategory.MrRelativePhase, label: 'MR Relative Phase' },
  { key: SeriesCriteriaCategory.CtKernel, label: 'CT Kernel' },
  { key: SeriesCriteriaCategory.PostProcessing, label: 'Post Processing' },
  { key: SeriesCriteriaCategory.Dimensionality, label: 'Dimensionality' },
  { key: SeriesCriteriaCategory.DocumentType, label: 'Document Type' },
];

export interface SeriesClassificationDisplayProps {
  seriesClassification: Array<{ category: string; value: string; displayName: string }>;
}

export const SeriesClassificationDisplay = ({
  seriesClassification,
}: SeriesClassificationDisplayProps) => {
  const classificationData = useMemo(
    () =>
      DISPLAY_FIELDS.map((field) => {
        const classifications = seriesClassification.filter(
          (classification) => classification.category === field.key
        );
        const isSpecified = classifications.length > 0;
        const value = isSpecified
          ? classifications
              .map((classification) => classification.displayName || classification.value)
              .join('\n')
          : NOT_SPECIFIED_TEXT;

        return {
          key: field.key,
          label: field.label,
          value,
          isSpecified,
        };
      }).sort((a, b) => {
        if (a.isSpecified === b.isSpecified) return 0;
        return a.isSpecified ? -1 : 1;
      }),
    [seriesClassification]
  );

  return (
    <div
      css={css`
        min-height: 100%;
        background-color: ${Colors.black};
        border: 1px solid ${Colors.gray4};
        border-radius: ${rootTheme.radius.xs}px;
      `}
    >
      {classificationData.map((field, index) => (
        <React.Fragment key={field.key}>
          <Stack
            alignY="center"
            css={css`
              padding: ${Spacings.xsmall}rem 16px;
            `}
          >
            <Text
              css={css`
                flex: 1;
                color: ${Colors.lightSubtler};
                line-height: 24px;
              `}
            >
              {field.label}
            </Text>
            <Text
              css={css`
                text-align: right;
                white-space: pre-line;
                ${field.value === NOT_SPECIFIED_TEXT
                  ? `color: ${Colors.lightSubtler};`
                  : `color: ${Colors.light};`}
              `}
              data-testid={`series-classification-field-${field.key}`}
            >
              {field.value}
            </Text>
          </Stack>
          {index < classificationData.length - 1 && (
            <Divider
              css={css`
                background-color: ${Colors.gray4};
              `}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
